import { Center, Stack, Flex, Button } from "@mantine/core";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import type {
  Site,
  SiteCategoriesResponse
} from "../../../utils/backend-types";
import { SearchBar } from "../../SearchBar/SearchBar";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { NotAccessableModal } from "../../StructureView/NotAccessableModal/NotAccessableModal";
import type { SiteSetupProcess } from "../hooks/useSiteSetupProcesses";
import { SiteModal } from "./SiteModal";
import { SitesList } from "./SitesList/SitesList";

interface SitesPageProps {
  projectId: string;
  sites: ReadonlyArray<Site>;
  siteSetupProcesses: ReadonlyArray<SiteSetupProcess>;
  onSiteCreated: (site: Site) => void;
  onSiteEdited: (site: Site) => void;
  onDeleteSite: (site: Site) => void;
  onDeleteSiteSetupProcess: (siteSetupProcess: SiteSetupProcess) => void;
  variantId: number;
  siteCategories: Array<SiteCategoriesResponse> | undefined;
}

function SitesPage({
  projectId,
  sites,
  siteSetupProcesses,
  onSiteCreated,
  onSiteEdited,
  onDeleteSite,
  onDeleteSiteSetupProcess,
  variantId,
  siteCategories
}: SitesPageProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalCreateMode, setIsModalCreateMode] = useState(true);
  const [siteToEdit, setSiteToEdit] = useState<Site | null>(null);
  const [autoModalOpen, setAutoModalOpen] = useState(false);
  const [noAccessModalOpen, setNoAccessModalOpen] = useState(false);
  const { shouldShowStaffView } = useShouldShowStaffView();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectedSiteId = searchParams.get("site");
  const [searchValue, setSearchValue] = useState("");

  function toggleModal() {
    setIsModalOpen((prev) => !prev);
  }

  function openCreateModal() {
    if (!shouldShowStaffView) {
      setNoAccessModalOpen(true);
    } else {
      setIsModalCreateMode(true);
      toggleModal();
    }
  }

  function openEditModal(site: Site) {
    setIsModalCreateMode(false);
    setSiteToEdit(site);
    toggleModal();
  }

  function handleSiteOrSetupProcessDelete(
    siteOrSetupProcess: Site | SiteSetupProcess
  ) {
    if ((siteOrSetupProcess as SiteSetupProcess).status) {
      onDeleteSiteSetupProcess(siteOrSetupProcess as SiteSetupProcess);
    } else {
      onDeleteSite(siteOrSetupProcess as Site);
    }
  }

  useEffect(() => {
    if (autoModalOpen) {
      return;
    }

    if (redirectedSiteId) {
      const site = sites?.find(
        (site) => site.id === parseInt(redirectedSiteId)
      );
      if (site) {
        setIsModalOpen(true);
        setIsModalCreateMode(false);
        setSiteToEdit(site);
        setAutoModalOpen(true);
      }
    }
  }, [autoModalOpen, redirectedSiteId, sites]);

  const filteredSites = searchValue
    ? sites.filter((site) =>
        site.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : sites;

  const filteredSetupProcesses = searchValue
    ? siteSetupProcesses.filter((process) =>
        process.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : siteSetupProcesses;

  return (
    <div className="SitesPage">
      {sites.length === 0 && siteSetupProcesses.length === 0 && (
        <Center>
          <CreateSiteButton
            noAccess={!shouldShowStaffView}
            size="lg"
            onClickCreate={openCreateModal}
          />
        </Center>
      )}
      {(sites.length > 0 || siteSetupProcesses.length > 0) && (
        <Stack>
          <Flex justify="space-between">
            <SearchBar
              btnHidden
              controlledValue={searchValue}
              placeholder="Suche nach Name"
              onChange={(event) => setSearchValue(event.currentTarget.value)}
              onClear={() => setSearchValue("")}
            />
            <CreateSiteButton
              noAccess={!shouldShowStaffView}
              onClickCreate={openCreateModal}
            />
          </Flex>
          <SitesList
            highlightTerm={searchValue}
            projectId={projectId}
            setupProcesses={filteredSetupProcesses}
            siteCategories={siteCategories}
            sites={filteredSites}
            onClickEdit={openEditModal}
            onDelete={handleSiteOrSetupProcessDelete}
          />
        </Stack>
      )}
      <SiteModal
        headerText={
          isModalCreateMode ? "Neue Liegenschaft" : "Liegenschaft bearbeiten"
        }
        isOpen={isModalOpen}
        site={!isModalCreateMode ? siteToEdit : null}
        variantId={variantId}
        onClickSave={isModalCreateMode ? onSiteCreated : onSiteEdited}
        onToggle={toggleModal}
      />
      {noAccessModalOpen && (
        <NotAccessableModal
          text={
            "Diese Option steht derzeit nicht zur Verfügung," +
            " da Sie nicht über die entsprechenden Berechtigungen verfügen." +
            " Bitte wenden Sie sich an das Support-Team: "
          }
          onClose={() => setNoAccessModalOpen(false)}
        />
      )}
    </div>
  );
}

function CreateSiteButton({
  noAccess,
  size,
  onClickCreate
}: {
  noAccess: boolean;
  size?: string;
  onClickCreate: () => void;
}) {
  return (
    <Button
      color="primary"
      id="create-site-button"
      size={size}
      variant={noAccess ? "default" : "filled"}
      onClick={onClickCreate}
    >
      Neue Liegenschaft
    </Button>
  );
}

export { SitesPage, SitesPageProps };
